import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { GlobalStore } from '../../store';
import User from '../../models/User';
import PageNotFound from '../NoMatch';
import { HTTP_SERVER_URL } from '../../configuration';

interface ApiKeyView {
  id: number;
  label: string;
  created_at: string;
  expires_at: string;
}

interface ApiKeyCreated extends ApiKeyView {
  token: string;
}

function getToken(): string {
  const raw = localStorage.getItem('Token');
  return raw ? JSON.parse(raw).token : '';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('sv-SE');
}

const pre = { background: '#f8f9fa', padding: '1em', borderRadius: '4px', fontSize: '0.82em', overflowX: 'auto' as const };

export default (): JSX.Element => {
  const user = useSelector<GlobalStore, User | null>(store => store.user);

  const [keys, setKeys] = useState<ApiKeyView[]>([]);
  const [keysLabel, setKeysLabel] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [keysError, setKeysError] = useState('');
  const [keysLoading, setKeysLoading] = useState(false);

  const authHeader = { Authorization: `Token ${getToken()}` };

  useEffect(() => {
    fetch(`${HTTP_SERVER_URL}/api/api_keys`, { headers: authHeader })
      .then(r => r.json())
      .then((data: ApiKeyView[]) => setKeys(data))
      .catch(() => setKeys([]));
  }, []);

  function handleGenerateKey(): void {
    if (!keysLabel.trim()) return;
    setKeysLoading(true);
    setKeysError('');
    setNewToken(null);
    fetch(`${HTTP_SERVER_URL}/api/api_keys`, {
      method: 'POST',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify({ label: keysLabel.trim() })
    })
      .then(r => r.ok ? r.json() : Promise.reject('Server error'))
      .then((data: ApiKeyCreated) => {
        setNewToken(data.token);
        setKeysLabel('');
        setKeys(prev => [{ id: data.id, label: data.label, created_at: data.created_at, expires_at: data.expires_at }, ...prev]);
      })
      .catch(() => setKeysError('Failed to generate key.'))
      .finally(() => setKeysLoading(false));
  }

  function handleRevokeKey(id: number): void {
    fetch(`${HTTP_SERVER_URL}/api/api_keys/${id}`, {
      method: 'DELETE',
      headers: authHeader
    })
      .then(() => setKeys(prev => prev.filter(k => k.id !== id)))
      .catch(() => setKeysError('Failed to revoke key.'));
  }

  function handleCopy(): void {
    if (!newToken) return;
    navigator.clipboard.writeText(newToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (user === null || !user.isTeacher()) return <PageNotFound />;

  return (
    <div className="container mt-4">

      <h2>API</h2>
      <p className="text-muted">
        API keys let you automate queue management from scripts — opening the queue,
        setting MOTD, uploading bookings, and more.
        Keys are valid for 365 days and use the same <code>Authorization: Token &lt;key&gt;</code> header as below.
      </p>

      {keysError && <div className="alert alert-danger">{keysError}</div>}

      {keys.length === 0
        ? <p className="text-muted">No keys yet.</p>
        : <table className="table table-sm table-bordered mb-3">
            <thead>
              <tr><th>Label</th><th>Created</th><th>Expires</th><th></th></tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id}>
                  <td>{k.label}</td>
                  <td>{formatDate(k.created_at)}</td>
                  <td>{formatDate(k.expires_at)}</td>
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => handleRevokeKey(k.id)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      }

      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Label — required (e.g. booking script)"
          value={keysLabel}
          onChange={e => setKeysLabel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleGenerateKey()}
        />
        <div className="input-group-append">
          <button
            className="btn btn-primary"
            onClick={handleGenerateKey}
            disabled={keysLoading || !keysLabel.trim()}>
            {keysLoading ? 'Generating…' : 'Generate key'}
          </button>
        </div>
      </div>

      {newToken && (
        <div className="alert alert-success mb-5">
          <strong>Key generated — copy it now, it won't be shown again.</strong>
          <div className="input-group mt-2">
            <input
              type="text"
              className="form-control"
              readOnly
              value={newToken}
              style={{ fontFamily: 'monospace', fontSize: '0.85em' }}
            />
            <div className="input-group-append">
              <button className="btn btn-outline-secondary" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <button className="btn btn-sm btn-link p-0 mt-1" onClick={() => setNewToken(null)}>
            Dismiss
          </button>
        </div>
      )}

      <hr />

      <h4>Common tasks</h4>
      <p className="text-muted" style={{ fontSize: '0.9em' }}>
        Replace <code>&lt;queue&gt;</code> with your queue name (URL-encoded if it contains spaces, e.g. <code>tilda%20(alba)</code>).
      </p>

      <h5>Open / close the queue</h5>
      <pre style={pre}>{
`# Open the queue (unlock)
curl -X DELETE https://queue.eecs.kth.se/api/queues/<queue>/lock \\
  -H "Authorization: Token <your-api-key>"

# Close the queue (lock)
curl -X POST https://queue.eecs.kth.se/api/queues/<queue>/lock \\
  -H "Authorization: Token <your-api-key>"`
      }</pre>

      <h5>Set message of the day (MOTD) and queue info</h5>
      <pre style={pre}>{
`# Set MOTD (shown prominently at the top of the queue)
curl -X PUT https://queue.eecs.kth.se/api/queues/<queue>/motd \\
  -H "Authorization: Token <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Lab 3 today. Bring your laptop."}'

# Set queue info (köinfo — shown in the queue sidebar)
curl -X PUT https://queue.eecs.kth.se/api/queues/<queue>/info \\
  -H "Authorization: Token <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Help with pointers and memory management."}'

# Clear MOTD
curl -X PUT https://queue.eecs.kth.se/api/queues/<queue>/motd \\
  -H "Authorization: Token <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"message": ""}'`
      }</pre>

      <h5>Upload bookings</h5>
      <pre style={pre}>{
`curl -X POST https://queue.eecs.kth.se/api/queues/<queue>/bookings \\
  -H "Authorization: Token <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '[
    {"provider_id": "u1abc123", "slot_time": "2026-03-01T10:00:00Z"},
    {"provider_id": "u1def456", "slot_time": "2026-03-01T10:15:00Z", "entry_type": "present"},
    {"provider_id": "u1ghi789", "slot_time": "2026-03-01T10:30:00Z", "entry_type": "help"}
  ]'`
      }</pre>
      <p className="text-muted" style={{ fontSize: '0.85em' }}>
        <code>provider_id</code> accepts either a KTH ID (<code>u1...</code>) or a plain username (e.g. <code>alba</code>).<br />
        <code>entry_type</code> defaults to <code>"present"</code> if omitted; use <code>"help"</code> for help sessions.<br />
        Returns <code>{`{"bookings": [...], "skipped": [...]}`}</code> — skipped entries could not be resolved.<br />
        For a working example that pulls from Canvas, see <a href="/examples/canvas_to_queue.py" download><code>canvas_to_queue.py</code></a>.
      </p>

      <h5>Delete bookings</h5>
      <pre style={pre}>{
`# Delete all future bookings
curl -X DELETE "https://queue.eecs.kth.se/api/queues/<queue>/bookings" \\
  -H "Authorization: Token <your-api-key>"

# Delete bookings for a specific day
curl -X DELETE "https://queue.eecs.kth.se/api/queues/<queue>/bookings?from=2026-04-01&until=2026-04-01" \\
  -H "Authorization: Token <your-api-key>"`
      }</pre>

      <h5>Add / remove teaching assistants</h5>
      <pre style={pre}>{
`# Add one assistant
curl -X POST https://queue.eecs.kth.se/api/queues/<queue>/assistants \\
  -H "Authorization: Token <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"username": "abcde"}'

# Remove one assistant
curl -X DELETE https://queue.eecs.kth.se/api/queues/<queue>/assistants/abcde \\
  -H "Authorization: Token <your-api-key>"`
      }</pre>

      <hr />

      <details>
        <summary style={{ cursor: 'pointer', userSelect: 'none' }}>
          <strong>Full API reference</strong>
        </summary>
        <div className="mt-3">

          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            Authenticate all requests with: <code>Authorization: Token &lt;your-api-key&gt;</code>.
            Managing API keys themselves (<code>/api/api_keys</code>) requires your browser JWT, not an API key.
          </p>
          <p className="text-muted" style={{ fontSize: '0.9em' }}>
            Queue names with spaces or special characters must be URL-encoded:
            spaces → <code>%20</code>, <code>(</code> → <code>%28</code>, <code>)</code> → <code>%29</code>.
            In a shell script: <code>python3 -c "import urllib.parse; print(urllib.parse.quote('QUEUE_NAME'))"</code>
          </p>

          <table className="table table-sm table-bordered mb-4" style={{ fontSize: '0.85em' }}>
            <thead className="thead-dark">
              <tr><th>Method</th><th>Path</th><th>Access</th><th>Description</th></tr>
            </thead>
            <tbody>
              <tr><td>GET</td><td><code>/api/queues</code></td><td>Public</td><td>All queues.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/mine</code></td><td>Any</td><td>Queues where the caller is teacher. Superadmin: all queues.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue</code></td><td>Public</td><td>Single queue info (name, locked, motd, info…).</td></tr>
              <tr><td>POST</td><td><code>/api/queues/:queue/lock</code></td><td>Teacher/TA</td><td>Lock (close) the queue.</td></tr>
              <tr><td>DELETE</td><td><code>/api/queues/:queue/lock</code></td><td>Teacher/TA</td><td>Unlock (open) the queue. Clears TA check-ins.</td></tr>
              <tr><td>PUT</td><td><code>/api/queues/:queue/motd</code></td><td>Teacher/TA</td><td>Set MOTD. Body: <code>{`{"message": "..."}`}</code>. Empty string to clear.</td></tr>
              <tr><td>PUT</td><td><code>/api/queues/:queue/info</code></td><td>Teacher/TA</td><td>Set queue info (köinfo). Body: <code>{`{"message": "..."}`}</code>.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/teachers</code></td><td>Teacher</td><td>List teachers for a queue.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/assistants</code></td><td>Teacher</td><td>List teaching assistants.</td></tr>
              <tr><td>POST</td><td><code>/api/queues/:queue/assistants</code></td><td>Teacher</td><td>Add one TA. Body: <code>{`{"username": "..."}`}</code>.</td></tr>
              <tr><td>DELETE</td><td><code>/api/queues/:queue/assistants/:username</code></td><td>Teacher</td><td>Remove one TA.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/bookings</code></td><td>Teacher/TA</td><td>List all bookings.</td></tr>
              <tr><td>POST</td><td><code>/api/queues/:queue/bookings</code></td><td>Teacher</td><td>Upload bookings (upsert). Body: array of <code>{`{"provider_id", "slot_time", "entry_type"}`}</code>.</td></tr>
              <tr><td>DELETE</td><td><code>/api/queues/:queue/bookings</code></td><td>Teacher</td><td>Delete future bookings. Optional: <code>?from=YYYY-MM-DD&until=YYYY-MM-DD</code>.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/interaction_log</code></td><td>Teacher</td><td>Help/present interactions. Filter: <code>?type=help|present</code>. Optional: <code>from</code>/<code>until</code>. Default: last 90 days.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/queue_log</code></td><td>Teacher</td><td>Join/leave event log. Optional: <code>from</code>/<code>until</code>. Default: last 90 days.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/queue_events</code></td><td>Teacher</td><td>Queue open/close event log. Optional: <code>from</code>/<code>until</code>. Default: last 90 days. Returns <code>{`[{"event_type": "open"|"close", "occurred_at": "..."}]`}</code>.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/statistics/sessions</code></td><td>Teacher</td><td>Session stats per day. Optional: <code>from</code>/<code>until</code>. Default: last 90 days.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/statistics/sessions/date/:date</code></td><td>Teacher</td><td>Session detail by date (<code>YYYY-MM-DD</code>). Merges multiple sessions on the same day.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/statistics/sessions/date/:date/hourly</code></td><td>Teacher</td><td>Hourly breakdown by date (<code>YYYY-MM-DD</code>). Merges multiple sessions on the same day.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/statistics/assistants</code></td><td>Teacher</td><td>Per-TA stats. Optional: <code>from</code>/<code>until</code>. Default: last 90 days.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/statistics/interaction_sessions</code></td><td>Teacher</td><td>Interactions grouped by session. Optional: <code>from</code>/<code>until</code>. Default: last 90 days.</td></tr>
              <tr><td>GET</td><td><code>/api/my_help_history</code></td><td>Any</td><td>Own help history (last 1.5 years). Fields: <code>queue</code>, <code>started_at</code>, <code>ended_at</code>, <code>duration_secs</code>, <code>helper</code>.</td></tr>
              <tr><td>POST</td><td><code>/api/queues/:queue/checkin</code></td><td>TA</td><td>Check in as on-duty TA.</td></tr>
              <tr><td>POST</td><td><code>/api/queues/:queue/checkout</code></td><td>TA</td><td>Check out (end session).</td></tr>
              <tr><td>DELETE</td><td><code>/api/queues/:queue/checkin</code></td><td>TA</td><td>Cancel check-in (removes open session without recording it).</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/my_checkins</code></td><td>TA</td><td>Own check-in sessions. Optional: <code>?from=&until=</code> (ISO 8601). Default: last 90 days.</td></tr>
              <tr><td>GET</td><td><code>/api/queues/:queue/ta_checkins/:username</code></td><td>Teacher</td><td>Check-in sessions for a specific TA. Optional: <code>?from=&until=</code>. Default: last 90 days.</td></tr>
            </tbody>
          </table>

        </div>
      </details>

    </div>
  );
};
